import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UsePreventUnsavedChangesDropOptions {
  /**
   * Whether to show the browser's beforeunload dialog when closing/refreshing
   * @default true
   */
  preventBeforeUnload?: boolean;
  /**
   * Custom message for the beforeunload dialog (browser may ignore this)
   * If not provided, will use the translated default message
   */
  beforeUnloadMessage?: string;
}

export interface UsePreventUnsavedChangesDropReturn {
  /**
   * Whether the unsaved changes dialog is currently open
   */
  isDialogOpen: boolean;
  /**
   * Function to manually close the dialog
   */
  closeDialog: () => void;
  /**
   * Function to manually open the dialog
   */
  openDialog: () => void;
  /**
   * Function to handle dialog confirmation (user wants to discard changes)
   */
  handleConfirm: () => void;
  /**
   * Function to handle dialog cancellation (user wants to keep editing)
   */
  handleCancel: () => void;
  /**
   * Function to trigger the protection dialog when trying to close
   */
  triggerProtection: () => void;
}

/**
 * Custom hook that prevents users from accidentally losing unsaved changes.
 * Shows a confirmation dialog when users try to close a dialog with unsaved changes.
 *
 * @param isDirty - Whether the form has unsaved changes
 * @param options - Configuration options
 * @returns Object with dialog control functions
 */
export function usePreventUnsavedChangesDrop(
  isDirty: boolean,
  options: UsePreventUnsavedChangesDropOptions = {}
): UsePreventUnsavedChangesDropReturn {
  const { t } = useTranslation('patterns');
  const { preventBeforeUnload = true, beforeUnloadMessage } = options;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Use translated message or fallback to custom message
  const defaultBeforeUnloadMessage = t(
    'UnsavedChangesDialog.beforeUnload',
    'You have unsaved changes. Are you sure you want to leave?'
  );
  const messageToUse = beforeUnloadMessage || defaultBeforeUnloadMessage;

  // Handle browser beforeunload (refresh, close tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty && preventBeforeUnload) {
        event.preventDefault();
        event.returnValue = messageToUse;
        return messageToUse;
      }
    };

    if (isDirty && preventBeforeUnload) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isDirty, preventBeforeUnload, messageToUse]);

  // In-app route blocking is handled by UnsavedChangesProtection via history.block.

  // Trigger protection dialog when trying to close a dialog
  const triggerProtection = useCallback(() => {
    if (isDirty) {
      setIsDialogOpen(true);
    }
  }, [isDirty]);

  // Handle dialog confirmation (user wants to discard changes)
  const handleConfirm = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  // Handle dialog cancellation (user wants to keep editing)
  const handleCancel = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  return {
    isDialogOpen,
    closeDialog,
    openDialog,
    handleConfirm,
    handleCancel,
    triggerProtection
  };
}
